import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaGithub } from 'react-icons/fa';
import { HiMail, HiPhone, HiLocationMarker } from 'react-icons/hi';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Logo from '../common/Logo';
import { prefersReducedMotion } from '../../hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);
  const blob1Ref  = useRef(null);
  const blob2Ref  = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion() || !footerRef.current) return;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: footerRef.current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        onUpdate: ({ progress }) => {
          if (blob1Ref.current) gsap.to(blob1Ref.current, { y: progress * -60, x: progress * 20, duration: 0.4, overwrite: 'auto', ease: 'power1.out' });
          if (blob2Ref.current) gsap.to(blob2Ref.current, { y: progress *  60, x: progress * -20, duration: 0.4, overwrite: 'auto', ease: 'power1.out' });
        },
      });
    }, footerRef);
    return () => ctx.revert();
  }, []);

  return (
    <footer ref={footerRef} className="relative overflow-hidden bg-paper-2 border-t border-hairline">
      {/* Soft gradient blobs */}
      <div ref={blob1Ref} className="absolute top-0 -left-32 w-[500px] h-[500px] rounded-full bg-nexcess-blue/5 blur-3xl pointer-events-none" style={{ willChange: 'transform' }} />
      <div ref={blob2Ref} className="absolute bottom-0 -right-32 w-[500px] h-[500px] rounded-full bg-nexcess-purple/5 blur-3xl pointer-events-none" style={{ willChange: 'transform' }} />

      <div className="container-custom relative z-10 pt-20 pb-10">
        {/* Top: brand + columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 pb-14 border-b border-hairline">
          <div data-reveal="up" className="col-span-2 md:col-span-1">
            <Logo size="lg" />
            <p className="mt-5 text-sm text-ink-soft leading-relaxed max-w-xs">
              Building innovative digital solutions that transform businesses and create
              exceptional user experiences.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: <FaFacebook />, href: 'https://facebook.com' },
                { icon: <FaTwitter />, href: 'https://twitter.com' },
                { icon: <FaInstagram />, href: 'https://instagram.com' },
                { icon: <FaLinkedin />, href: 'https://linkedin.com' },
                { icon: <FaGithub />, href: 'https://github.com' },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-white border border-hairline flex items-center justify-center text-ink-soft hover:text-nexcess-blue hover:border-nexcess-blue/40 hover:-translate-y-0.5 hover:shadow-soft transition-all duration-300"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          <div data-reveal="up" data-reveal-delay="0.1">
            <div className="text-sm font-semibold text-ink mb-5">Quick Links</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-ink-soft hover:text-nexcess-blue transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-ink-soft hover:text-nexcess-blue transition-colors">About</Link></li>
              <li><Link to="/services" className="text-ink-soft hover:text-nexcess-blue transition-colors">Services</Link></li>
              <li><Link to="/projects" className="text-ink-soft hover:text-nexcess-blue transition-colors">Projects</Link></li>
              <li><Link to="/contact" className="text-ink-soft hover:text-nexcess-blue transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div data-reveal="up" data-reveal-delay="0.2">
            <div className="text-sm font-semibold text-ink mb-5">Services</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/services/web-development" onClick={() => localStorage.setItem('condition', 'web')} className="text-ink-soft hover:text-nexcess-blue transition-colors">Web Development</Link></li>
              <li><Link to="/services/mobile-app-development" onClick={() => localStorage.setItem('condition', 'mobile')} className="text-ink-soft hover:text-nexcess-blue transition-colors">Mobile Development</Link></li>
              <li><Link to="/services/cloud-services" onClick={() => localStorage.setItem('condition', 'cloud')} className="text-ink-soft hover:text-nexcess-blue transition-colors">Cloud Services</Link></li>
              <li><Link to="/services/ai-integration" onClick={() => localStorage.setItem('condition', 'ai')} className="text-ink-soft hover:text-nexcess-blue transition-colors">AI Integration</Link></li>
            </ul>
          </div>

          <div data-reveal="up" data-reveal-delay="0.3">
            <div className="text-sm font-semibold text-ink mb-5">Get in Touch</div>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3 text-ink-soft">
                <span className="w-8 h-8 rounded-lg bg-white border border-hairline flex items-center justify-center text-nexcess-blue shrink-0">
                  <HiLocationMarker />
                </span>
                <span className="leading-relaxed">1095, Silver Business Point, VIP Circle, Utran, Surat 394105</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-white border border-hairline flex items-center justify-center text-nexcess-blue shrink-0">
                  <HiPhone />
                </span>
                <a href="tel:+917984069470" className="text-ink-soft hover:text-nexcess-blue transition-colors">+91 79840 69470</a>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-white border border-hairline flex items-center justify-center text-nexcess-blue shrink-0">
                  <HiMail />
                </span>
                <a href="mailto:Info@nexcessinfotech.com" className="text-ink-soft hover:text-nexcess-blue transition-colors break-all">Info@nexcessinfotech.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-5 pt-8">
          <div className="text-xs text-ink-soft">
            © {new Date().getFullYear()} Nexcess Infotech. All Rights Reserved.
          </div>
          <div className="flex items-center gap-6 text-xs">
            <Link to="/privacy-policy" className="text-ink-soft hover:text-nexcess-blue transition-colors">Privacy Policy</Link>
            <Link to="/terms-conditions" className="text-ink-soft hover:text-nexcess-blue transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>

      {/* Subtle gradient brand mark */}
      <div className="absolute -bottom-10 md:-bottom-16 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="ghost-mark text-center" style={{ fontSize: 'clamp(6rem, 22vw, 20rem)' }}>
          NEXCESS
        </div>
      </div>
    </footer>
  );
};

export default Footer;
